import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GBELogo } from "@/components/layout/GBELogo";
import { Loader2, ShieldCheck, AlertOctagon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [invite, setInvite] = useState(null);   // { email, name, role_label, valid, expires_at }
  const [loadErr, setLoadErr] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLoadErr("Missing invitation token."); return; }
    api.get(`/invites/${token}`)
       .then((r) => setInvite(r.data))
       .catch((e) => setLoadErr(e.response?.data?.detail || "Invitation not found."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (password.length < 8)  { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/invites/${token}/accept`, { password });
      setDone(true);
      toast.success("Account created — please sign in");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create account");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-6">
      <div className="w-full max-w-[460px]">
        <div className="mb-8 flex justify-center">
          <div className="rounded-lg px-4 py-3 bg-gbe"><GBELogo /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-8">
          {loadErr && (
            <div data-testid="invite-error" className="text-center">
              <AlertOctagon className="w-8 h-8 text-rose-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Invitation unavailable</h1>
              <p className="text-sm text-slate-500 mt-2">{loadErr}</p>
              <Button onClick={() => nav("/login")} variant="outline" className="mt-6">Back to sign in</Button>
            </div>
          )}

          {!loadErr && !invite && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {!loadErr && invite && !invite.valid && (
            <div data-testid="invite-expired" className="text-center">
              <AlertOctagon className="w-8 h-8 text-amber-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Invitation {invite.status}</h1>
              <p className="text-sm text-slate-500 mt-2">
                This invitation link is no longer usable. Ask your administrator to send a new one.
              </p>
              <Button onClick={() => nav("/login")} variant="outline" className="mt-6">Back to sign in</Button>
            </div>
          )}

          {!loadErr && invite && invite.valid && !done && (
            <>
              <div className="mb-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gbe font-medium">
                  <ShieldCheck className="w-4 h-4" /> Accept invitation
                </div>
                <h1 className="font-display text-2xl font-semibold text-slate-900 mt-2">Welcome, {invite.name}</h1>
                <p className="text-sm text-slate-500 mt-1">
                  You've been invited to join the GBE Group Data Hub as
                  <span className="font-medium text-slate-700"> {invite.role_label}</span>.
                  Set a password to activate your account.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-slate-700">Email</Label>
                  <Input value={invite.email} disabled className="mt-1.5 bg-slate-50" />
                </div>
                <div>
                  <Label htmlFor="pw" className="text-slate-700">Choose a password</Label>
                  <Input
                    id="pw"
                    data-testid="invite-pw"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5"
                    placeholder="Min. 8 characters, must include a letter and a digit"
                  />
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-slate-700">Confirm password</Label>
                  <Input
                    id="pw2"
                    data-testid="invite-pw-confirm"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="invite-submit"
                  disabled={busy || password.length < 8 || password !== confirm}
                  className="w-full bg-gbe hover:bg-gbe-hover text-white h-10"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                </Button>
                <p className="text-xs text-slate-500 text-center pt-2">
                  After signing in, please enable two-factor authentication on your Security page.
                </p>
              </form>
            </>
          )}

          {done && (
            <div className="text-center py-6" data-testid="invite-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <h1 className="font-display text-xl font-semibold text-slate-900 mt-3">Account created</h1>
              <p className="text-sm text-slate-500 mt-2">Redirecting you to sign in…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
